import React from 'react';
import moment from 'moment';
import 'moment-timezone';
import PropTypes from 'prop-types';
import { Col, Input, Row, Spinner } from 'reactstrap';

import api from '../../../constants/api';
import { useLocaleConfig } from '../../../context/AppContext';
import { bookingTeamName } from '../../../components/fleetnexis/slotbooking/bookingTeam';
import TopBar from '../../../components/fleetnexis/slotbooking/TopBar';

/*
 * Team Slot Schedule — read-only 30-minute grid of every team's day.
 *
 * Self-contained on purpose: adds no shared components and edits no other file.
 * Styles live in the scoped block below so the global stylesheet is untouched.
 *
 * Source: POST /locationsystemtest/getLocationForMapTest
 *   { data: [ { employee: {name, eid, day, shift_start, shift_end, location},
 *               timeZones: [{id, time_zone}], bookings: [...] } ] }
 * One element is ONE SHIFT, so a team working two shifts appears twice; rows are
 * merged by team name below.
 */

const SLOT_MINUTES = 30;
const DEFAULT_RANGE = { start: 0, end: 24 * 60 };
const COL = 76; // px per 30-minute slot

// The schedule module stores a non-working day as the literal string 'off'
// (see schedule/Index.js getTotalHours). 'Select' is the unsaved placeholder.
// These must never be treated as "unknown", or an OFF team gets painted as
// available from its timeZones grid.
const OFF_TOKENS = new Set(['off', 'select', 'none', '']);
const isOffToken = (value) => OFF_TOKENS.has(String(value ?? '').trim().toLowerCase());

const toMinutes = (value) => {
  if (isOffToken(value)) return null;
  const parts = String(value).split(':');
  const hours = Number(parts[0]);
  const mins = Number(parts[1]);
  return Number.isFinite(hours) && Number.isFinite(mins) ? hours * 60 + mins : null;
};

const toClock = (minutes) => {
  if (minutes == null || !Number.isFinite(minutes)) return '';
  const safe = Math.max(0, Math.round(minutes));
  const totalHours = Math.floor(safe / 60) % 24;
  const mins = safe % 60;
  const meridiem = totalHours >= 12 ? 'PM' : 'AM';
  const hour12 = totalHours % 12 === 0 ? 12 : totalHours % 12;
  return `${String(hour12).padStart(2, '0')}:${String(mins).padStart(2, '0')} ${meridiem}`;
};

const mergeRanges = (ranges) => {
  const sorted = ranges
    .filter((range) => range.start != null && range.end != null && range.end > range.start)
    .sort((a, b) => a.start - b.start);
  return sorted.reduce((acc, range) => {
    const last = acc[acc.length - 1];
    if (last && range.start <= last.end) {
      last.end = Math.max(last.end, range.end);
      return acc;
    }
    return [...acc, { ...range }];
  }, []);
};

const TeamSlotSchedule = ({ bookingStats, countOfDistancesLessThan10 }) => {
  const { timezone, dateFormat } = useLocaleConfig();
  const zone = timezone || 'Asia/Kolkata';

  const today = React.useCallback(() => moment().tz(zone).format('YYYY-MM-DD'), [zone]);
  const [date, setDate] = React.useState(today);
  const [rows, setRows] = React.useState([]);
  const [slotBookings, setSlotBookings] = React.useState([]);
  // booking id -> real duration in minutes, summed from its services.
  const [durations, setDurations] = React.useState({});
  const [roster, setRoster] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [search, setSearch] = React.useState('');
  const [teamFilter, setTeamFilter] = React.useState('');
  const [hideOff, setHideOff] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [nowMinutes, setNowMinutes] = React.useState(null);

  React.useEffect(() => {
    api
      .get('/driverhelper/getDriverHelper')
      .then((res) => setRoster(Array.isArray(res.data?.data) ? res.data.data : []))
      .catch(() => setRoster([]));
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    setSelected(null);
    setSlotBookings([]);
    setDurations({});

    const day = moment(date).format('dddd').toLowerCase();

    api
      .post('/locationsystemtest/getLocationForMapTest', {
        zone_id: '1',
        date,
        day,
        prefered_time: '',
        next_day: moment(date).add(1, 'days').format('dddd').toLowerCase(),
        next_date: moment(date).add(1, 'days').format('YYYY-MM-DD'),
      })
      .then((res) => {
        if (cancelled) return;
        const payload = res.data?.elements || res.data?.data || [];
        const list = Array.isArray(payload) ? payload : [];
        setRows(list);
        if (!list.length) setError('No team schedules were returned for this date.');
      })
      .catch(() => {
        if (!cancelled) {
          setRows([]);
          setError('Unable to load the schedule. Please try again.');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    // Bookings come from the slot-table endpoint, not from the schedule
    // response: getLocationForMapTest's `bookings` array has been empty in
    // every sample, so a real booking never reached the grid.
    api
      .post('/slottablebookings/getSlotBookings', { date })
      .then((res) => {
        if (cancelled) return;
        const groups = res.data?.elements || res.data?.data || [];
        const flat = [];
        (Array.isArray(groups) ? groups : []).forEach((group) => {
          const slotTime = group?.parent_key;
          (Array.isArray(group?.booking) ? group.booking : []).forEach((b) => {
            flat.push({ ...b, slotTime });
          });
        });
        setSlotBookings(flat);

        // The booking row stores no duration — addBooking never sends one, so
        // the estimate only ever lived in the browser during slot search. It is
        // rebuilt the same way Edit Booking does: sum sp_estimated_time across
        // the booking's services.
        // Bookings with a saved duration_minutes need no lookup.
        const ids = Array.from(
          new Set(flat.filter((b) => !(parseInt(b?.duration_minutes, 10) > 0)).map((b) => b?.bid).filter(Boolean)),
        );
        Promise.allSettled(
          ids.map((id) =>
            api
              .get('/slotbooking/getServicesOfBooking', { params: { booking_id: id } })
              .then((r) => {
                const services = Array.isArray(r.data?.data) ? r.data.data : [];
                // getServicesOfBooking exposes the duration as sp_estimated_time
                // (from service_packages, the table add_package_id points at).
                // pakage_estimate_time is the legacy field and is NULL for rows
                // whose id does not happen to exist in `pakage`, so it is only a
                // fallback.
                // Add-on rows carry their own time in sa_duration (sp_* is
                // only joined for main services).
                const minutes = services.reduce(
                  (sum, item) =>
                    sum +
                    (parseInt(
                      Number(item?.is_addon) === 1
                        ? item?.sa_duration
                        : item?.sp_estimated_time ?? item?.pakage_estimate_time,
                      10,
                    ) || 0),
                  0,
                );
                return { id, minutes };
              }),
          ),
        ).then((results) => {
          if (cancelled) return;
          const map = {};
          results.forEach((result) => {
            if (result.status === 'fulfilled' && result.value.minutes > 0) {
              map[result.value.id] = result.value.minutes;
            }
          });
          setDurations(map);
        });
      })
      .catch(() => {
        if (!cancelled) setSlotBookings([]);
      });

    return () => {
      cancelled = true;
    };
  }, [date]);

  React.useEffect(() => {
    const tick = () => {
      const isToday = date === moment().tz(zone).format('YYYY-MM-DD');
      setNowMinutes(isToday ? toMinutes(moment().tz(zone).format('HH:mm')) : null);
    };
    tick();
    const id = setInterval(tick, 60000);
    return () => clearInterval(id);
  }, [date, zone]);

  const unmatchedRef = React.useRef([]);

  const technicianCount = React.useCallback(
    (teamName) => {
      const group = roster.find((item) => String(item?.name) === String(teamName));
      return Array.isArray(group?.items) ? group.items.length : 0;
    },
    [roster],
  );

  const teams = React.useMemo(() => {
    const byTeam = new Map();

    rows.forEach((row) => {
      const emp = row?.employee || {};
      const name = row?.team ?? emp.name;
      if (!name) return;

      if (!byTeam.has(name)) {
        byTeam.set(name, {
          name,
          id: emp.eid ?? row?.id ?? '',
          location: emp.location || row?.latlong || '',
          shifts: [],
          bookings: [],
        });
      }
      const entry = byTeam.get(name);

      // Explicitly OFF: record nothing. Falling through to the timeZones
      // fallback here is what previously showed availability for off teams.
      const markedOff = isOffToken(emp.shift_start) || isOffToken(emp.shift_end);
      if (!markedOff) {
        const start = toMinutes(emp.shift_start);
        let end = toMinutes(emp.shift_end);
        // A shift running past midnight (e.g. 22:00-02:00, or ending 00:00) is
        // drawn to the end of this day; the remainder belongs to the next date.
        if (start != null && end != null && end <= start) end = 24 * 60;
        if (start != null && end != null && end > start) {
          entry.shifts.push({ start, end });
        } else if (emp.shift_start === undefined && Array.isArray(row?.timeZones) && row.timeZones.length) {
          // Only when the API omits shift bounds entirely.
          const times = row.timeZones.map((tz) => toMinutes(tz.time_zone)).filter((t) => t != null);
          if (times.length) {
            entry.shifts.push({ start: Math.min(...times), end: Math.max(...times) + SLOT_MINUTES });
          }
        }
      }

      // Backend contract (locationSystemService.getBookingsByEmpId):
      //   prefered_time        -> time_zones.id, NOT a clock string
      //   pakage_estimate_time -> duration in minutes
      // So the slot id is resolved through this row's own timeZones list.
      const slotById = new Map((row?.timeZones || []).map((tz) => [String(tz.id), tz.time_zone]));

      (Array.isArray(row?.bookings) ? row.bookings : []).forEach((booking) => {
        const clock =
          slotById.get(String(booking?.prefered_time)) || booking?.time_zone || booking?.slot;
        const bStart = toMinutes(clock);
        const duration = Number.parseInt(
          booking?.pakage_estimate_time ?? booking?.avai ?? booking?.duration ?? 0,
          10,
        );
        if (bStart == null) return;
        entry.bookings.push({
          start: bStart,
          end: bStart + (Number.isFinite(duration) && duration > 0 ? duration : SLOT_MINUTES),
          estimated: Number.isFinite(duration) && duration > 0,
          ref: booking?.id || booking?.service_id || booking?.booking_id || '',
          name: booking?.name || '',
          phone: booking?.phone || '',
          latlong: booking?.latlong || '',
          service: booking?.pakage_id ? `Package ${booking.pakage_id}` : '',
        });
      });
    });

    // Attach the bookings that came from /slottablebookings/getSlotBookings.
    const leftovers = [];
    slotBookings.forEach((b) => {
      const teamKey = bookingTeamName(b) ?? b?.assigned_to ?? b?.team;
      const start = toMinutes(b?.time_zone || b?.slotTime || b?.slot || b?.prefered_time);
      if (start == null) return;
      const entry = byTeam.get(String(teamKey));
      const saved = Number.parseInt(b?.duration_minutes, 10);
      const resolved = (Number.isFinite(saved) && saved > 0 ? saved : 0) || durations[b?.bid];
      const raw = Number.parseInt(b?.avai ?? b?.duration ?? 0, 10);
      const duration = resolved || (Number.isFinite(raw) && raw > 0 ? raw : 0);
      const record = {
        start,
        end: start + (duration > 0 ? duration : SLOT_MINUTES),
        estimated: duration > 0,
        ref: b?.bid || b?.service_id || '',
        name: b?.bname || b?.name || '',
        phone: b?.bphone || b?.phone || '',
        latlong: b?.latlong || '',
        status: b?.booking_status || '',
        service: b?.pakage_name || '',
      };
      if (entry) entry.bookings.push(record);
      else leftovers.push({ ...record, team: String(teamKey ?? 'unknown') });
    });
    unmatchedRef.current = leftovers;

    return Array.from(byTeam.values())
      .map((team) => {
        const shifts = mergeRanges(team.shifts);
        const workedMinutes = shifts.reduce((sum, s) => sum + (s.end - s.start), 0);
        return {
          ...team,
          shifts,
          workedMinutes,
          isOff: shifts.length === 0,
          bookings: Array.from(
            new Map(team.bookings.map((b) => [`${b.start}-${b.ref}`, b])).values(),
          ).sort((a, b) => a.start - b.start),
          technicians: technicianCount(team.name),
        };
      })
      .sort((a, b) => String(a.name).localeCompare(String(b.name), undefined, { numeric: true }));
  }, [rows, slotBookings, durations, technicianCount]);

  const visibleTeams = React.useMemo(() => {
    const query = search.trim().toLowerCase();
    return teams.filter((team) => {
      if (hideOff && team.isOff) return false;
      const matchesTeam = !teamFilter || String(team.name) === teamFilter;
      const matchesSearch = !query || String(team.name).toLowerCase().includes(query);
      return matchesTeam && matchesSearch;
    });
  }, [teams, search, teamFilter, hideOff]);

  const range = React.useMemo(() => {
    const points = [];
    teams.forEach((team) => {
      team.shifts.forEach((shift) => points.push(shift.start, shift.end));
      team.bookings.forEach((booking) => points.push(booking.start, booking.end));
    });
    if (!points.length) return DEFAULT_RANGE;
    const start = Math.min(DEFAULT_RANGE.start, Math.floor(Math.min(...points) / 60) * 60);
    const end = Math.max(DEFAULT_RANGE.end, Math.ceil(Math.max(...points) / 60) * 60);
    return { start, end: Math.max(end, start + 60) };
  }, [teams]);

  // Every column is exactly SLOT_MINUTES wide.
  const slots = React.useMemo(() => {
    const list = [];
    for (let m = range.start; m < range.end; m += SLOT_MINUTES) list.push(m);
    return list;
  }, [range]);

  const cellFor = (team, slotStart) => {
    const slotEnd = slotStart + SLOT_MINUTES;
    const booking = team.bookings.find((b) => slotStart < b.end && slotEnd > b.start);
    if (booking) {
      return { type: 'booking', booking, isStart: booking.start >= slotStart && booking.start < slotEnd };
    }
    if (team.shifts.some((s) => slotStart >= s.start && slotStart < s.end)) return { type: 'available' };
    return { type: 'off' };
  };

  const stats = React.useMemo(() => {
    const totalBookings = teams.reduce((sum, team) => sum + team.bookings.length, 0);
    const offTeams = teams.filter((team) => team.isOff).length;
    const totalTechnicians = teams.reduce((sum, team) => sum + team.technicians, 0);
    if (nowMinutes == null) {
      return { totalTechnicians, totalTeams: teams.length, offTeams, available: null, busy: null, totalBookings };
    }
    let available = 0;
    let busy = 0;
    teams.forEach((team) => {
      const cell = cellFor(team, Math.floor(nowMinutes / SLOT_MINUTES) * SLOT_MINUTES);
      if (cell.type === 'booking') busy += 1;
      else if (cell.type === 'available') available += 1;
    });
    return { totalTechnicians, totalTeams: teams.length, offTeams, available, busy, totalBookings };
  }, [teams, nowMinutes]);

  const shiftLabel = (team) => {
    if (team.isOff) return 'OFF — no shift scheduled';
    return team.shifts.map((s) => `${toClock(s.start)} - ${toClock(s.end)}`).join(', ');
  };

  const stepDate = (days) => setDate(moment(date).add(days, 'days').format('YYYY-MM-DD'));

  // A day whose earliest shift is 00:00 opens on hours of empty hatching, so
  // jump the scroller to the first slot anyone is actually working.
  const scrollRef = React.useRef(null);
  const firstBusySlot = React.useMemo(() => {
    const points = [];
    visibleTeams.forEach((team) => {
      team.shifts.forEach((shift) => points.push(shift.start));
      team.bookings.forEach((booking) => points.push(booking.start));
    });
    return points.length ? Math.min(...points) : null;
  }, [visibleTeams]);

  React.useEffect(() => {
    const node = scrollRef.current;
    if (!node || firstBusySlot == null) return;
    const index = Math.max(0, Math.floor((firstBusySlot - range.start) / SLOT_MINUTES) - 1);
    node.scrollLeft = Math.max(0, index * COL - COL / 2);
  }, [firstBusySlot, range.start, loading]);

  const pxPerMin = COL / SLOT_MINUTES;
  const x = (minutes) => (minutes - range.start) * pxPerMin;
  const trackWidth = slots.length * COL;

  return (
    <>
      <style>{`
        .tss-wrap { --tss-line:#e5e7eb; --tss-muted:#6b7280; --tss-ink:#111827; }
        .tss-card { background:#fff; border:1px solid var(--tss-line); border-radius:10px; min-width:0; }
        .tss-bar { display:flex; flex-wrap:wrap; gap:10px; align-items:center; padding:14px 16px; margin-bottom:14px; }
        .tss-btn { border:1px solid var(--tss-line); background:#fff; border-radius:8px; height:38px; padding:0 14px; font-size:14px; color:var(--tss-ink); }
        .tss-btn:hover { background:#f8f9fb; }
        .tss-stats { display:grid; gap:12px; grid-template-columns:repeat(auto-fit,minmax(150px,1fr)); margin-bottom:14px; }
        .tss-stat { padding:14px 16px; }
        .tss-stat-label { font-size:12px; color:var(--tss-muted); display:flex; align-items:center; gap:7px; }
        .tss-stat-value { font-size:22px; font-weight:700; margin-top:4px; font-variant-numeric:tabular-nums; }
        .tss-dot { width:9px; height:9px; border-radius:50%; display:inline-block; }
        .tss-head { display:flex; flex-wrap:wrap; gap:12px; justify-content:space-between; align-items:center; padding:14px 16px; border-bottom:1px solid var(--tss-line); }
        .tss-legend { display:flex; flex-wrap:wrap; gap:16px; font-size:12px; color:var(--tss-muted); }
        .tss-legend span { display:inline-flex; align-items:center; gap:6px; }

        /* Col is a flex item: without min-width:0 it stretches to the full track
           width and the scroller never overflows, so nothing scrolls. */
        .tss-col { min-width:0; max-width:100%; }
        .tss-scroll { overflow-x:auto; width:100%; max-width:100%; -webkit-overflow-scrolling:touch; }
        .tss-scroll::-webkit-scrollbar { height:10px; }
        .tss-scroll::-webkit-scrollbar-thumb { background:#c7ccd4; border-radius:6px; }
        .tss-scroll::-webkit-scrollbar-track { background:#f1f3f6; border-radius:6px; }

        .tss-grid { width:max-content; min-width:100%; }
        .tss-row { display:flex; border-bottom:1px solid #eef1f5; }
        .tss-row:last-child { border-bottom:0; }
        .tss-side { position:sticky; left:0; z-index:3; background:#fff; width:230px; min-width:230px; padding:10px 14px; border-right:1px solid var(--tss-line); }
        .tss-hdr .tss-side { background:#f8f9fb; display:flex; align-items:center; font-size:12px; color:var(--tss-muted); font-weight:600; }
        .tss-hdr { background:#f8f9fb; border-bottom:1px solid var(--tss-line); }
        .tss-track { position:relative; height:78px; background:repeating-linear-gradient(45deg,#fafbfc,#fafbfc 6px,#f1f3f6 6px,#f1f3f6 12px); }
        .tss-track-hdr { height:36px; background:none; }
        .tss-gl { position:absolute; top:0; bottom:0; width:1px; background:#eef1f5; }
        .tss-gl.is-hour { background:#e3e7ee; }
.tss-tick {
  position: absolute;
  top: 11px;
  padding-left: 6px;         
  font-size: 11px;
  color: var(--tss-muted);
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  z-index: 1;
}


        .tss-tick.is-hour { color:var(--tss-ink); font-weight:600; }

        /* continuous availability band + spanning booking bars */
        .tss-avail { position:absolute; top:6px; bottom:6px; background:#eaf7f0; border-top:1px solid #d8ecdf; border-bottom:1px solid #d8ecdf; }
        .tss-bk { position:absolute; top:12px; bottom:12px; border:1px solid #b9cdf5; background:#eaf1fe; border-radius:6px; padding:6px 8px; text-align:left; overflow:hidden; cursor:pointer; }
        .tss-bk:hover { background:#dde8fd; }
        .tss-bk.is-on { border-color:#1b4fd8; box-shadow:0 0 0 1px #1b4fd8; }
        .tss-bk-time { display:block; font-size:11px; font-weight:600; color:#1f2a44; white-space:nowrap; font-variant-numeric:tabular-nums; }
        .tss-bk-ref { display:block; font-size:11px; color:#3d5a91; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .tss-now { position:absolute; top:0; bottom:0; width:0; border-left:2px dashed #e11d48; z-index:2; }
        .tss-nowchip { display:inline-block; background:#e11d48; color:#fff; font-size:11px; font-weight:600; padding:2px 8px; border-radius:4px; transform:translateX(-50%); font-variant-numeric:tabular-nums; }
        .tss-nowrow { position:relative; height:26px; }

        .tss-team { font-weight:600; font-size:14px; }
        .tss-sub { font-size:12px; color:var(--tss-muted); margin-top:2px; }
        .tss-offbadge { display:inline-block; background:#fdecec; color:#c0392b; font-size:11px; font-weight:600; padding:1px 7px; border-radius:4px; }
        .tss-hrsbadge { display:inline-block; background:#e9f7ef; color:#12805c; font-size:11px; font-weight:600; padding:1px 7px; border-radius:4px; }
        .tss-empty { padding:44px 16px; text-align:center; color:var(--tss-muted); font-size:14px; }
        .tss-note { font-size:12px; color:var(--tss-muted); padding:12px 16px; border-top:1px solid var(--tss-line); }
        .tss-detail { padding:16px; }
        .tss-kv { display:flex; justify-content:space-between; gap:12px; font-size:13px; padding:5px 0; border-bottom:1px solid #f3f5f8; }
        .tss-kv span:first-child { color:var(--tss-muted); }
      `}</style>

      

      <div className="tss-wrap">
        <div className="tss-card tss-bar">
          <div>
            <div style={{ fontSize: 18, fontWeight: 700 }}>Technician Schedule</div>
            <div style={{ fontSize: 13, color: '#6C757D' }}>
              Every team&apos;s day on a 30-minute grid
            </div>
          </div>
          <div className="d-flex flex-wrap gap-2 ms-auto align-items-center">
            <button type="button" className="tss-btn" onClick={() => setDate(today())}>Today</button>
            <button type="button" className="tss-btn" onClick={() => stepDate(-1)} aria-label="Previous day">&lsaquo;</button>
            <button type="button" className="tss-btn" onClick={() => stepDate(1)} aria-label="Next day">&rsaquo;</button>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} style={{ width: 170, height: 38 }} />
          </div>
        </div>

        <div className="tss-card tss-bar">
          <Input type="select" value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} style={{ maxWidth: 190, height: 38 }}>
            <option value="">All Teams</option>
            {teams.map((team) => (
              <option key={team.name} value={team.name}>{team.name}</option>
            ))}
          </Input>
          <Input type="search" placeholder="Search team..." value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 260, height: 38 }} />
          <label className="d-inline-flex align-items-center gap-2 mb-0" style={{ fontSize: 13 }} htmlFor="tss-hideoff">
            <input id="tss-hideoff" type="checkbox" checked={hideOff} onChange={(e) => setHideOff(e.target.checked)} />
            Hide OFF teams
          </label>
          <span className="ms-auto" style={{ fontSize: 13, color: '#6C757D' }}>
            {moment(date).format(dateFormat || 'DD-MM-YYYY')} &middot; {visibleTeams.length} of {teams.length} teams
          </span>
        </div>

        <div className="tss-stats">
          <div className="tss-card tss-stat">
            <div className="tss-stat-label">Teams</div>
            <div className="tss-stat-value">{stats.totalTeams}</div>
          </div>
          <div className="tss-card tss-stat">
            <div className="tss-stat-label"><i className="tss-dot" style={{ background: '#c0392b' }} /> OFF Today</div>
            <div className="tss-stat-value">{stats.offTeams}</div>
          </div>
          <div className="tss-card tss-stat">
            <div className="tss-stat-label">Technicians</div>
            <div className="tss-stat-value">{stats.totalTechnicians || '—'}</div>
          </div>
          <div className="tss-card tss-stat">
            <div className="tss-stat-label"><i className="tss-dot" style={{ background: '#16a34a' }} /> Available Now</div>
            <div className="tss-stat-value">{stats.available == null ? '—' : stats.available}</div>
          </div>
          <div className="tss-card tss-stat">
            <div className="tss-stat-label"><i className="tss-dot" style={{ background: '#1b4fd8' }} /> Busy Now</div>
            <div className="tss-stat-value">{stats.busy == null ? '—' : stats.busy}</div>
          </div>
          <div className="tss-card tss-stat">
            <div className="tss-stat-label">Bookings This Day</div>
            <div className="tss-stat-value">{stats.totalBookings}</div>
          </div>
        </div>

        {bookingStats && (
          <TopBar
            bookingStats={bookingStats}
            countOfDistancesLessThan10={countOfDistancesLessThan10}
          />
        )}

        <Row>
          <Col lg={selected ? 9 : 12} className="tss-col">
            <div className="tss-card">
              <div className="tss-head">
                <strong style={{ fontSize: 15 }}>Schedule Timeline &mdash; 30 min slots</strong>
                <div className="tss-legend">
                  <span><i className="tss-dot" style={{ background: '#8fd3ac' }} /> Available</span>
                  <span><i className="tss-dot" style={{ background: '#9db8ee' }} /> Booking</span>
                  <span><i className="tss-dot" style={{ background: '#d1d5db' }} /> OFF / outside hours</span>
                  {nowMinutes != null && <span><i className="tss-dot" style={{ background: '#e11d48' }} /> Now</span>}
                </div>
              </div>

              {loading ? (
                <div className="tss-empty"><Spinner size="sm" /> Loading schedule…</div>
              ) : visibleTeams.length === 0 ? (
                <div className="tss-empty">{error || 'No teams match the current filter.'}</div>
              ) : (
                <div className="tss-scroll" ref={scrollRef}>
                  <div className="tss-grid">
                    <div className="tss-row tss-hdr">
                      <div className="tss-side">Technician / Team</div>
                      <div className="tss-track tss-track-hdr" style={{ width: trackWidth }}>
                        {slots.map((slot, index) => (
                          <React.Fragment key={slot}>
                            <span className={`tss-gl ${slot % 60 === 0 ? 'is-hour' : ''}`} style={{ left: index * COL }} />
                                                       <span
                              className={`tss-tick ${slot % 60 === 0 ? 'is-hour' : ''}`}
                              style={{ left: index * COL }}
                            >
                              {toClock(slot)}
                            </span>
                          </React.Fragment>
                        ))}
                      </div>
                    </div>

                    {visibleTeams.map((team) => (
                      <div className="tss-row" key={team.name}>
                        <div className="tss-side">
                          <div className="tss-team">Team {team.name}</div>
                          <div className="tss-sub">
                            {team.isOff ? (
                              <span className="tss-offbadge">OFF</span>
                            ) : (
                              <span className="tss-hrsbadge">
                                {Math.round((team.workedMinutes / 60) * 10) / 10} hours
                              </span>
                            )}
                            {team.technicians ? ` · ${team.technicians} tech` : ''}
                          </div>
                          <div className="tss-sub">{shiftLabel(team)}</div>
                        </div>

                        <div className="tss-track" style={{ width: trackWidth }}>
                          {slots.map((slot, index) => (
                            <span key={slot} className={`tss-gl ${slot % 60 === 0 ? 'is-hour' : ''}`} style={{ left: index * COL }} />
                          ))}

                          {team.shifts.map((shift) => (
                            <span
                              key={`av-${shift.start}`}
                              className="tss-avail"
                              style={{ left: x(shift.start), width: Math.max(0, x(shift.end) - x(shift.start)) }}
                            />
                          ))}

                          {team.bookings.map((booking) => {
                            const key = `${team.name}-${booking.start}-${booking.ref}`;
                            const width = Math.max(58, x(booking.end) - x(booking.start));
                            return (
                              <button
                                type="button"
                                key={key}
                                className={`tss-bk ${selected?.key === key ? 'is-on' : ''}`}
                                style={{ left: x(booking.start), width }}
                                onClick={() => setSelected({ key, team, booking })}
                                title={`${toClock(booking.start)} - ${toClock(booking.end)}`}
                              >
                                <span className="tss-bk-time">
                                  {toClock(booking.start)} - {toClock(booking.end)}
                                </span>
                                <span className="tss-bk-ref">
                                  {booking.ref ? `Booking #${booking.ref}` : 'Booking'}
                                </span>
                              </button>
                            );
                          })}

                          {nowMinutes != null && nowMinutes >= range.start && nowMinutes <= range.end && (
                            <span className="tss-now" style={{ left: x(nowMinutes) }} />
                          )}
                        </div>
                      </div>
                    ))}

                    {nowMinutes != null && nowMinutes >= range.start && nowMinutes <= range.end && (
                      <div className="tss-row" style={{ borderBottom: 0 }}>
                        <div className="tss-side" style={{ padding: 0, height: 26 }} />
                        <div className="tss-nowrow" style={{ width: trackWidth }}>
                          <span className="tss-nowchip" style={{ position: 'absolute', left: x(nowMinutes), top: 2 }}>
                            {toClock(nowMinutes)}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {unmatchedRef.current.length > 0 && (
                <div className="tss-note" style={{ color: '#b45309', background: '#fdf3e3' }}>
                  {unmatchedRef.current.length} booking(s) could not be matched to a team row —
                  assigned to: {Array.from(new Set(unmatchedRef.current.map((b) => b.team))).join(', ')}.
                  They exist in View Bookings but that team has no schedule for this date.
                </div>
              )}

              <div className="tss-note">
                Grid lines mark 30-minute slots; darker lines are hours. Green is scheduled working
                time, blue bars are bookings spanning their full duration, hatched is OFF or outside
                working hours. Scroll sideways for the rest of the day.
              </div>
            </div>
          </Col>

          {selected && (
            <Col lg={3} className="tss-col">
              <div className="tss-card tss-detail">
                <div className="d-flex justify-content-between align-items-start mb-2">
                  <strong style={{ fontSize: 13 }}>Booking details</strong>
                  <button type="button" className="tss-btn" style={{ height: 28, padding: '0 10px' }} onClick={() => setSelected(null)}>
                    Close
                  </button>
                </div>
                <div className="tss-kv"><span>Team</span><span>{selected.team.name}</span></div>
                <div className="tss-kv"><span>Reference</span><span>{selected.booking.ref || '—'}</span></div>
                <div className="tss-kv"><span>Time</span><span>{toClock(selected.booking.start)} - {toClock(selected.booking.end)}</span></div>
                <div className="tss-kv">
                  <span>Duration</span>
                  <span>
                    {selected.booking.end - selected.booking.start} min
                    {!selected.booking.estimated && (
                      <em style={{ color: '#b45309', fontStyle: 'normal' }}> (assumed)</em>
                    )}
                  </span>
                </div>
                <div className="tss-kv"><span>Service</span><span>{selected.booking.service || '—'}</span></div>
                <div className="tss-kv"><span>Customer</span><span>{selected.booking.name || '—'}</span></div>
                <div className="tss-kv"><span>Phone</span><span>{selected.booking.phone || '—'}</span></div>
                <div className="tss-kv"><span>Status</span><span>{selected.booking.status || '—'}</span></div>
                <div className="tss-kv" style={{ borderBottom: 0 }}>
                  <span>Team base</span>
                  <span style={{ textAlign: 'right', wordBreak: 'break-all' }}>{selected.team.location || '—'}</span>
                </div>
              </div>
            </Col>
          )}
        </Row>
      </div>
    </>
  );
};

TeamSlotSchedule.propTypes = {
  bookingStats: PropTypes.shape({
    pending: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    completed: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    paymentPending: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  countOfDistancesLessThan10: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default TeamSlotSchedule;
